'use client';

import * as React from 'react';
import { Settings2, Calendar, Pause, Play, XCircle, RefreshCw, LayoutGrid, Loader2, Shield } from 'lucide-react';

import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ProcessorSelector } from '@/components/form';
import { DatePicker } from '@/components/ui/date-picker';
import { cn } from "@/lib/utils";

import type { ActionConfig, ActionType, BulkCancelReason } from '../types';

interface ActionPanelProps {
  action: ActionConfig | null;
  onActionChange: (action: ActionConfig | null) => void;
  options?: {
    stores: Array<{ id: string; name: string }>;
    processors: Array<{ id: string; name: string; type: string }>;
    statuses: string[];
    paymentTypes: string[];
    failureReasons: string[];
  };
}

const ACTION_OPTIONS: Array<{
  type: ActionType;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  {
    type: 'processor_change',
    label: 'Change Processor',
    description: 'Switch subscriptions to a different payment processor',
    icon: Settings2,
  },
  {
    type: 'billing_update',
    label: 'Update Billing Date',
    description: 'Modify billing cycle anchor or next billing date',
    icon: Calendar,
  },
  {
    type: 'distribute_rebills',
    label: 'Distribute Rebills',
    description: 'Spread billing dates evenly over a period',
    icon: LayoutGrid,
  },
  {
    type: 'pause',
    label: 'Pause',
    description: 'Temporarily stop billing for subscriptions',
    icon: Pause,
  },
  {
    type: 'resume',
    label: 'Resume',
    description: 'Restart paused or past-due subscriptions',
    icon: Play,
  },
  {
    type: 'cancel',
    label: 'Cancel',
    description: 'Cancel subscriptions',
    icon: XCircle,
  },
  {
    type: 'retry_new_processor',
    label: 'Retry on New Processor',
    description: 'Change processor and trigger immediate rebill',
    icon: RefreshCw,
  },
  {
    type: 'set_tagada_vault',
    label: 'Set Tagada Vault',
    description: 'Activate or deactivate Tagada Vault for subscriptions',
    icon: Shield,
  },
];

export function ActionPanel({
  action,
  onActionChange,
  options,
}: ActionPanelProps) {
  // Set action type with default config
  const handleTypeSelect = (type: ActionType) => {
    switch (type) {
      case 'processor_change':
        onActionChange({ type: 'processor_change', targetProcessorId: '' });
        break;
      case 'billing_update':
        onActionChange({ type: 'billing_update', billingMode: 'keep_anchor' });
        break;
      case 'distribute_rebills':
        onActionChange({ type: 'distribute_rebills', distributionPeriodDays: 7 });
        break;
      case 'pause':
        onActionChange({ type: 'pause' });
        break;
      case 'resume':
        onActionChange({ type: 'resume', resumeBehavior: 'reset' });
        break;
      case 'cancel':
        onActionChange({ type: 'cancel', cancelAtPeriodEnd: true });
        break;
      case 'retry_new_processor':
        onActionChange({
          type: 'retry_new_processor',
          targetProcessorId: '',
          resetRetryCounter: true,
          billImmediately: true,
        });
        break;
      case 'set_tagada_vault':
        onActionChange({ type: 'set_tagada_vault', useTagadaVault: true });
        break;
    }
  };

  // Render config form for selected action
  const renderActionConfig = () => {
    if (!action) return null;

    switch (action.type) {
      case 'processor_change':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs">Target Processor</Label>
              <ProcessorSelector
                processorId={action.targetProcessorId || undefined}
                onProcessorChange={(id) =>
                  onActionChange({ ...action, targetProcessorId: id || '' })
                }
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="migrate-immediately"
                checked={action.migrateImmediately || false}
                onCheckedChange={(checked) =>
                  onActionChange({ ...action, migrateImmediately: checked })
                }
              />
              <Label htmlFor="migrate-immediately" className="text-sm">
                Migrate immediately
              </Label>
            </div>
          </div>
        );

      case 'billing_update':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs">Mode</Label>
              <RadioGroup
                value={action.billingMode}
                onValueChange={(value) =>
                  onActionChange({ ...action, billingMode: value as any })
                }
                className="space-y-2"
              >
                {/* `one_time_override` is still accepted by the backend for operations recorded
                    before this screen dropped it, but it did exactly what "keep original anchor"
                    does, so offering both only made the panel ambiguous. */}
                <label className="flex items-center space-x-2 cursor-pointer">
                  <RadioGroupItem value="keep_anchor" />
                  <span className="text-sm">Move this attempt only, keep the billing day</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <RadioGroupItem value="change_anchor" />
                  <span className="text-sm">Move this attempt and the recurring billing day</span>
                </label>
              </RadioGroup>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">New Date</Label>
              <DatePicker
                value={action.newDate ? new Date(action.newDate) : undefined}
                onChange={(date) =>
                  onActionChange({ ...action, newDate: date?.toISOString() })
                }
              />
            </div>
          </div>
        );

      case 'distribute_rebills':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs">Distribution Period (days)</Label>
              <Input
                type="number"
                min={1}
                max={31}
                className="h-9"
                value={action.distributionPeriodDays}
                onChange={(e) =>
                  onActionChange({
                    ...action,
                    distributionPeriodDays: parseInt(e.target.value) || 7,
                  })
                }
              />
              <p className="text-xs text-muted-foreground">
                Spread billing dates evenly over this many days
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="update-anchor"
                checked={action.updateAnchor || false}
                onCheckedChange={(checked) =>
                  onActionChange({ ...action, updateAnchor: checked })
                }
              />
              <Label htmlFor="update-anchor" className="text-sm">
                Update anchor permanently
              </Label>
            </div>
          </div>
        );

      case 'pause':
        return (
          <div className="p-3 bg-muted/50 rounded-md">
            <p className="text-sm text-muted-foreground">
              Selected subscriptions will be paused. Billing will stop until they are resumed.
            </p>
          </div>
        );

      case 'resume':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs">Resume Behavior</Label>
              <RadioGroup
                value={action.resumeBehavior || 'reset'}
                onValueChange={(value) =>
                  onActionChange({ ...action, resumeBehavior: value as any })
                }
                className="space-y-2"
              >
                <label className="flex items-center space-x-2 cursor-pointer">
                  <RadioGroupItem value="reset" />
                  <span className="text-sm">Reset (start fresh billing cycle)</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <RadioGroupItem value="preserve" />
                  <span className="text-sm">Preserve (keep original anchor)</span>
                </label>
              </RadioGroup>
            </div>
            {action.resumeBehavior === 'reset' && (
              <div className="space-y-2">
                <Label className="text-xs">Days from today</Label>
                <Input
                  type="number"
                  min={0}
                  max={365}
                  className="h-9"
                  placeholder="0 (today)"
                  value={action.resetDays || ''}
                  onChange={(e) =>
                    onActionChange({
                      ...action,
                      resetDays: parseInt(e.target.value) || undefined,
                    })
                  }
                />
              </div>
            )}
          </div>
        );

      case 'cancel':
        return (
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Switch
                id="cancel-at-period-end"
                checked={action.cancelAtPeriodEnd ?? true}
                onCheckedChange={(checked) =>
                  onActionChange({ ...action, cancelAtPeriodEnd: checked })
                }
              />
              <Label htmlFor="cancel-at-period-end" className="text-sm">
                Cancel at period end
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              {action.cancelAtPeriodEnd
                ? 'Subscriptions will remain active until their current period ends'
                : 'Subscriptions will be canceled immediately'}
            </p>
            <div className="space-y-2">
              <Label className="text-xs">Reason</Label>
              {/* The merchant's cancellation report groups on this, so a batch closed for the wrong
                  reason is a wrong answer to "why did we lose this subscriber" (#TAG-2526). */}
              <RadioGroup
                value={action.cancelReason ?? ''}
                onValueChange={(value) =>
                  onActionChange({ ...action, cancelReason: value as BulkCancelReason })
                }
                className="space-y-2"
              >
                <label className="flex items-center space-x-2 cursor-pointer">
                  <RadioGroupItem value="unbilled_too_long" />
                  <span className="text-sm">Never billed for months, written off</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <RadioGroupItem value="scheduled_period_end" />
                  <span className="text-sm">The subscriber asked to stop at period end</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <RadioGroupItem value="merchant_request" />
                  <span className="text-sm">The merchant asked for it</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <RadioGroupItem value="customer_request" />
                  <span className="text-sm">The customer asked for it</span>
                </label>
              </RadioGroup>
            </div>
          </div>
        );

      case 'retry_new_processor':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs">Target Processor</Label>
              <ProcessorSelector
                processorId={action.targetProcessorId || undefined}
                onProcessorChange={(id) =>
                  onActionChange({ ...action, targetProcessorId: id || '' })
                }
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="reset-retry-counter"
                checked={action.resetRetryCounter ?? true}
                onCheckedChange={(checked) =>
                  onActionChange({ ...action, resetRetryCounter: checked })
                }
              />
              <Label htmlFor="reset-retry-counter" className="text-sm">
                Reset retry counter
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="bill-immediately"
                checked={action.billImmediately ?? true}
                onCheckedChange={(checked) =>
                  onActionChange({ ...action, billImmediately: checked })
                }
              />
              <Label htmlFor="bill-immediately" className="text-sm">
                Bill immediately
              </Label>
            </div>
          </div>
        );

      case 'set_tagada_vault':
        return (
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Switch
                id="use-tagada-vault"
                checked={action.useTagadaVault}
                onCheckedChange={(checked) =>
                  onActionChange({ ...action, useTagadaVault: checked })
                }
              />
              <Label htmlFor="use-tagada-vault" className="text-sm">
                Enable Tagada Vault
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              {action.useTagadaVault
                ? 'Tagada Vault will be activated for selected subscriptions'
                : 'Tagada Vault will be deactivated for selected subscriptions'}
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <Label className="text-xs font-medium uppercase text-muted-foreground">
          Action
        </Label>
        <p className="text-xs text-muted-foreground mt-1">
          Select an action to perform on matched subscriptions
        </p>
      </div>

      {/* Action Selection */}
      <div className="space-y-2">
        {ACTION_OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const isSelected = action?.type === opt.type;

          return (
            <div key={opt.type}>
              {/* Action Option */}
              <button
                type="button"
                onClick={() => handleTypeSelect(opt.type)}
                className={cn(
                  'w-full flex items-center gap-3 rounded-lg border p-3 text-left transition-colors',
                  isSelected
                    ? 'border-primary bg-primary/5'
                    : 'hover:bg-muted/50 hover:border-muted-foreground/30'
                )}
              >
                <Icon
                  className={cn(
                    'h-5 w-5 shrink-0',
                    isSelected ? 'text-primary' : 'text-muted-foreground'
                  )}
                />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{opt.label}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {opt.description}
                  </div>
                </div>
              </button>

              {/* Inline Config - Shows when selected */}
              {isSelected && (
                <div className="ml-5 mt-3 pl-3 border-l-2 border-muted pb-3">
                  {renderActionConfig()}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
